import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import React, { ChangeEvent, Component, DragEvent, createRef } from 'react';

import { uploadProgramImage } from '../../api/programs';
import ProgramImageOverwriteDialog from './ProgramImageOverwriteDialog';

const PROGRAM_IMAGE_UPLOAD_PREVIEW_HEIGHT = 180;
const PROGRAM_IMAGE_UPLOAD_MIN_HEIGHT = 260;

interface Props {
    disabled?: boolean;
    imageUrl?: string;
    onImageUploaded: (imageUrl: string) => void;
    title: string;
}

interface State {
    errorMessage?: string;
    hasLoadError: boolean;
    isDragging: boolean;
    isUploading: boolean;
    pendingFile?: File;
    showOverrideDialog: boolean;
    uploadedFileName?: string;
}

class ProgramImageUploadDropzone extends Component<Props, State> {
    private fileInputRef = createRef<HTMLInputElement>();

    state: State = {
        errorMessage: undefined,
        hasLoadError: false,
        isDragging: false,
        isUploading: false,
        pendingFile: undefined,
        showOverrideDialog: false,
        uploadedFileName: undefined,
    };

    componentDidUpdate(prevProps: Props) {
        if (prevProps.imageUrl !== this.props.imageUrl && this.state.hasLoadError) {
            this.setState({ hasLoadError: false });
        }
    }

    onBrowseClick = () => {
        this.fileInputRef.current?.click();
    };

    onImageError = () => {
        this.setState({ hasLoadError: true });
    };

    onImageLoaded = () => {
        if (this.state.hasLoadError) {
            this.setState({ hasLoadError: false });
        }
    };

    onDragEnter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.props.disabled) {
            return;
        }

        this.setState({ errorMessage: undefined, isDragging: true });
    };

    onDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ isDragging: false });
    };

    onDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    onDrop = async (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ isDragging: false });

        if (this.props.disabled) {
            return;
        }

        const droppedFile = event.dataTransfer.files?.[0];

        if (!droppedFile) {
            return;
        }

        await this.processSelectedFile(droppedFile);
    };

    onFileInputChanged = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) {
            return;
        }

        await this.processSelectedFile(selectedFile);
        event.target.value = '';
    };

    onOverrideCancel = () => {
        this.setState({ pendingFile: undefined, showOverrideDialog: false });
    };

    onOverrideConfirm = async () => {
        const { pendingFile } = this.state;

        if (!pendingFile) {
            return;
        }

        await this.uploadFile(pendingFile, true);
    };

    async processSelectedFile(file: File) {
        if (!file.type.startsWith('image/')) {
            this.setState({ errorMessage: 'Please choose a valid image file.' });
            return;
        }

        this.setState({ errorMessage: undefined, uploadedFileName: file.name });
        await this.uploadFile(file, false);
    }

    async uploadFile(file: File, overrideExistingFile: boolean) {
        this.setState({ errorMessage: undefined, isUploading: true, pendingFile: file, showOverrideDialog: false });

        try {
            const uploadResult = await uploadProgramImage(file, overrideExistingFile);

            this.props.onImageUploaded(uploadResult.imageUrl);
            this.setState({
                errorMessage: undefined,
                isUploading: false,
                pendingFile: undefined,
                showOverrideDialog: false,
                uploadedFileName: uploadResult.fileName,
            });
        } catch (error: unknown) {
            const conflictError = error as Error & { conflict?: boolean };

            if (conflictError.conflict) {
                this.setState({ isUploading: false, pendingFile: file, showOverrideDialog: true });
                return;
            }

            this.setState({
                errorMessage: error instanceof Error ? error.message : 'Unable to upload the program image.',
                isUploading: false,
                pendingFile: undefined,
                showOverrideDialog: false,
            });
        }
    }

    render() {
        const { disabled, imageUrl, title } = this.props;
        const { errorMessage, hasLoadError, isDragging, isUploading, pendingFile, showOverrideDialog, uploadedFileName } = this.state;
        const showPreview = Boolean(imageUrl) && !hasLoadError;
        const statusMessage = isUploading
            ? `Uploading ${pendingFile?.name || 'image'}...`
            : uploadedFileName
                ? `Last uploaded file: ${uploadedFileName}`
                : showPreview
                    ? `Previewing ${title}`
                    : 'No image uploaded yet.';

        return (
            <>
                <Paper
                    onDragEnter={this.onDragEnter}
                    onDragLeave={this.onDragLeave}
                    onDragOver={this.onDragOver}
                    onDrop={this.onDrop}
                    sx={{
                        backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.06)' : 'background.default',
                        border: '1px dashed',
                        borderColor: isDragging ? 'primary.main' : 'divider',
                        borderRadius: 3,
                        minHeight: PROGRAM_IMAGE_UPLOAD_MIN_HEIGHT,
                        mt: 1,
                        overflow: 'hidden',
                    }}
                    variant="outlined"
                >
                    <input accept="image/*" hidden onChange={this.onFileInputChanged} ref={this.fileInputRef} type="file" />
                    {showPreview && (
                        <Box
                            alt={title}
                            component="img"
                            onError={this.onImageError}
                            onLoad={this.onImageLoaded}
                            src={imageUrl}
                            sx={{
                                backgroundColor: 'grey.100',
                                display: 'block',
                                height: PROGRAM_IMAGE_UPLOAD_PREVIEW_HEIGHT,
                                objectFit: 'cover',
                                width: '100%',
                            }}
                        />
                    )}
                    {!showPreview && (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'grid',
                                gap: 1.25,
                                justifyItems: 'center',
                                minHeight: PROGRAM_IMAGE_UPLOAD_PREVIEW_HEIGHT,
                                p: 2.5,
                                textAlign: 'center',
                            }}
                        >
                            <CloudUploadRoundedIcon color={isDragging ? 'primary' : 'action'} sx={{ fontSize: 34 }} />
                            <Typography sx={{ fontWeight: 700 }} variant="body1">
                                {hasLoadError ? 'Image preview unavailable for this URL.' : 'Drop an image here to upload to Firebase Storage'}
                            </Typography>
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem', maxWidth: 360 }} variant="body2">
                                {hasLoadError
                                    ? 'You can replace this image by dragging and dropping a new file here or choosing a file below.'
                                    : 'Stored under /programs. Existing file names are blocked unless you explicitly override them.'}
                            </Typography>
                            <Button disabled={disabled || isUploading} onClick={this.onBrowseClick} size="small" variant="outlined">Choose File</Button>
                        </Box>
                    )}
                    <Box
                        sx={{
                            borderTop: showPreview ? '1px solid' : 'none',
                            borderColor: 'divider',
                            display: 'grid',
                            gap: showPreview ? 0.75 : 1,
                            px: showPreview ? 1.5 : 2,
                            py: showPreview ? 1 : 0,
                        }}
                    >
                        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                            {isUploading ? <CircularProgress size={18} /> : <InsertDriveFileRoundedIcon color="action" fontSize="small" />}
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }} variant="body2">
                                {statusMessage}
                            </Typography>
                        </Box>
                        {showPreview && (
                            <Box sx={{ alignItems: { md: 'center', xs: 'flex-start' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 0.75, justifyContent: 'space-between' }}>
                                <Typography color="text.secondary" sx={{ fontSize: '0.78rem', pr: 1 }} variant="body2">
                                    This preview also accepts drag and drop. Drop a new image here to replace the current one.
                                </Typography>
                                <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.5, justifyContent: 'flex-end' }}>
                                    <Button
                                        href={imageUrl}
                                        rel="noopener noreferrer"
                                        size="small"
                                        startIcon={<OpenInNewRoundedIcon />}
                                        sx={{ minWidth: 0, px: 1, py: 0.35, whiteSpace: 'nowrap' }}
                                        target="_blank"
                                        variant="text"
                                    >
                                        Open Image
                                    </Button>
                                    <Button
                                        disabled={disabled || isUploading}
                                        onClick={this.onBrowseClick}
                                        size="small"
                                        sx={{ minWidth: 0, px: 1, py: 0.35, whiteSpace: 'nowrap' }}
                                        variant="outlined"
                                    >
                                        Choose File
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                    </Box>
                </Paper>
                <ProgramImageOverwriteDialog
                    fileName={pendingFile?.name}
                    onCancel={this.onOverrideCancel}
                    onConfirm={this.onOverrideConfirm}
                    saving={isUploading}
                    visible={showOverrideDialog}
                />
            </>
        );
    }
}

export default ProgramImageUploadDropzone;